import { useCallback } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from './useAuth';
import {
  EmulatorSavePayloadKind,
  EmulatorSaveType,
  SaveQueueEntry,
  useSaveQueue,
} from './useSaveQueue';

const SAVE_BUCKET = 'emulator-saves';
const SCREENSHOT_URL_TTL_SECONDS = 60 * 60;
const MAX_SAVES_PER_ROM = 10;

export interface CloudSaveRom {
  id: string;
  title: string;
  system_id: string;
}

export interface EmulatorSaveStateRecord {
  id: string;
  user_id: string;
  rom_id: string;
  system_id: string;
  type: EmulatorSaveType;
  storage_path: string;
  screenshot_path: string | null;
  size_bytes: number;
  created_at: string;
  notes: string | null;
}

export interface CloudSaveListItem extends EmulatorSaveStateRecord {
  status: 'synced' | 'pending';
  screenshot_url: string | null;
}

interface SaveStateArgs {
  rom: CloudSaveRom;
  type: EmulatorSaveType;
  payloadKind: EmulatorSavePayloadKind;
  stateBlob: Blob;
  screenshotBlob?: Blob | null;
  notes?: string | null;
}

const createPendingScreenshotUrl = (entry: SaveQueueEntry) => {
  if (!entry.screenshotBuffer) {
    return null;
  }

  return URL.createObjectURL(new Blob([entry.screenshotBuffer], { type: 'image/jpeg' }));
};

const toPendingListItem = (entry: SaveQueueEntry): CloudSaveListItem => ({
  id: entry.id,
  user_id: entry.userId,
  rom_id: entry.romId,
  system_id: entry.systemId,
  type: entry.type,
  storage_path: entry.storagePath,
  screenshot_path: entry.screenshotPath,
  size_bytes: entry.sizeBytes,
  created_at: entry.createdAt,
  notes: entry.notes,
  status: 'pending',
  screenshot_url: createPendingScreenshotUrl(entry),
});

export const useCloudSaves = () => {
  const { user } = useAuth();
  const supabaseAny = supabase as any;

  const uploadQueuedEntry = useCallback(
    async (entry: SaveQueueEntry) => {
      if (!user) {
        throw new Error('Sign in to sync queued save states.');
      }

      const stateBlob = new Blob([entry.stateBuffer], { type: 'application/octet-stream' });
      const screenshotBlob = entry.screenshotBuffer
        ? new Blob([entry.screenshotBuffer], { type: 'image/jpeg' })
        : null;

      const { error: saveUploadError } = await supabase.storage.from(SAVE_BUCKET).upload(entry.storagePath, stateBlob, {
        upsert: true,
        cacheControl: '60',
        contentType: 'application/octet-stream',
      });

      if (saveUploadError) {
        throw saveUploadError;
      }

      if (entry.screenshotPath && screenshotBlob) {
        const { error: screenshotUploadError } = await supabase.storage
          .from(SAVE_BUCKET)
          .upload(entry.screenshotPath, screenshotBlob, {
            upsert: true,
            cacheControl: '60',
            contentType: screenshotBlob.type || 'image/jpeg',
          });

        if (screenshotUploadError) {
          throw screenshotUploadError;
        }
      }

      const { error: insertError } = await supabaseAny.from('emulator_save_states').upsert(
        {
          id: entry.id,
          user_id: user.id,
          rom_id: entry.romId,
          system_id: entry.systemId,
          type: entry.type,
          storage_path: entry.storagePath,
          screenshot_path: entry.screenshotPath,
          size_bytes: entry.sizeBytes,
          created_at: entry.createdAt,
          notes: entry.notes,
        },
        {
          onConflict: 'id',
        }
      );

      if (insertError) {
        throw insertError;
      }

      await enforceRetention(entry.romId);
    },
    [supabaseAny, user]
  );

  const saveQueue = useSaveQueue({
    enabled: Boolean(user),
    syncEntry: uploadQueuedEntry,
  });

  const buildSavePaths = useCallback(
    (romId: string, saveId: string) => {
      if (!user) {
        throw new Error('Sign in to save your game progress.');
      }

      return {
        storagePath: `${user.id}/${romId}/${saveId}.state`,
        screenshotPath: `${user.id}/${romId}/previews/${saveId}.jpg`,
      };
    },
    [user]
  );

  const createSignedScreenshotUrl = useCallback(async (path: string | null) => {
    if (!path) {
      return null;
    }

    const { data, error } = await supabase.storage.from(SAVE_BUCKET).createSignedUrl(path, SCREENSHOT_URL_TTL_SECONDS);
    if (error) {
      console.error('Failed to create signed save preview URL:', error);
      return null;
    }

    return data?.signedUrl ?? null;
  }, []);

  const listSaves = useCallback(
    async (romId: string, limit = MAX_SAVES_PER_ROM) => {
      const pendingItems = saveQueue.entries.filter((entry) => entry.romId === romId).map(toPendingListItem);

      if (!user) {
        return pendingItems.slice(0, limit);
      }

      const { data, error } = await supabaseAny
        .from('emulator_save_states')
        .select('*')
        .eq('user_id', user.id)
        .eq('rom_id', romId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        throw error;
      }

      const syncedItems = await Promise.all(
        ((data ?? []) as EmulatorSaveStateRecord[]).map(async (record) => ({
          ...record,
          status: 'synced' as const,
          screenshot_url: await createSignedScreenshotUrl(record.screenshot_path),
        }))
      );

      return [...syncedItems, ...pendingItems]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, limit);
    },
    [createSignedScreenshotUrl, saveQueue.entries, supabaseAny, user]
  );

  const enforceRetention = useCallback(
    async (romId: string) => {
      if (!user) {
        return;
      }

      const { data, error } = await supabaseAny
        .from('emulator_save_states')
        .select('*')
        .eq('user_id', user.id)
        .eq('rom_id', romId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      const saves = (data ?? []) as EmulatorSaveStateRecord[];
      if (saves.length <= MAX_SAVES_PER_ROM) {
        return;
      }

      const overflowCount = saves.length - MAX_SAVES_PER_ROM;
      const oldestFirst = [...saves].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      const autoCandidates = oldestFirst.filter((save) => save.type === 'auto');
      const manualCandidates = oldestFirst.filter((save) => save.type === 'manual');
      const removable = [...autoCandidates, ...manualCandidates].slice(0, overflowCount);

      for (const save of removable) {
        const { error: deleteRowError } = await supabaseAny
          .from('emulator_save_states')
          .delete()
          .eq('id', save.id)
          .eq('user_id', user.id);

        if (deleteRowError) {
          throw deleteRowError;
        }

        const paths = [save.storage_path, save.screenshot_path].filter(Boolean) as string[];
        if (paths.length > 0) {
          const { error: storageDeleteError } = await supabase.storage.from(SAVE_BUCKET).remove(paths);
          if (storageDeleteError) {
            console.error('Failed to prune emulator save assets:', storageDeleteError);
          }
        }
      }
    },
    [supabaseAny, user]
  );

  const saveState = useCallback(
    async ({ rom, type, payloadKind, stateBlob, screenshotBlob = null, notes = null }: SaveStateArgs) => {
      if (!user) {
        throw new Error('Sign in to save your progress to the cloud.');
      }

      const saveId = crypto.randomUUID();
      const createdAt = new Date().toISOString();
      const { storagePath, screenshotPath } = buildSavePaths(rom.id, saveId);
      const stateBuffer = await stateBlob.arrayBuffer();
      const screenshotBuffer = screenshotBlob ? await screenshotBlob.arrayBuffer() : null;

      const queueEntry: SaveQueueEntry = {
        id: saveId,
        userId: user.id,
        romId: rom.id,
        romTitle: rom.title,
        systemId: rom.system_id,
        type,
        notes,
        createdAt,
        sizeBytes: stateBlob.size,
        payloadKind,
        storagePath,
        screenshotPath: screenshotBuffer ? screenshotPath : null,
        stateBuffer,
        screenshotBuffer,
      };

      if (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine) {
        await saveQueue.enqueue(queueEntry);
        return toPendingListItem(queueEntry);
      }

      try {
        await uploadQueuedEntry(queueEntry);
        return {
          id: saveId,
          user_id: user.id,
          rom_id: rom.id,
          system_id: rom.system_id,
          type,
          storage_path: storagePath,
          screenshot_path: screenshotBuffer ? screenshotPath : null,
          size_bytes: stateBlob.size,
          created_at: createdAt,
          notes,
          status: 'synced',
          screenshot_url: screenshotBuffer ? await createSignedScreenshotUrl(screenshotPath) : null,
        } satisfies CloudSaveListItem;
      } catch (error) {
        await saveQueue.enqueue(queueEntry);
        return toPendingListItem(queueEntry);
      }
    },
    [buildSavePaths, createSignedScreenshotUrl, saveQueue, uploadQueuedEntry, user]
  );

  const loadStateBlob = useCallback(
    async (save: CloudSaveListItem) => {
      if (save.status === 'pending') {
        const pendingEntry = await saveQueue.getEntry(save.id);
        if (!pendingEntry) {
          throw new Error('The pending save could not be found locally.');
        }

        return new Blob([pendingEntry.stateBuffer], { type: 'application/octet-stream' });
      }

      const { data, error } = await supabase.storage.from(SAVE_BUCKET).download(save.storage_path);
      if (error) {
        throw error;
      }

      return data;
    },
    [saveQueue]
  );

  const getLatestAutosave = useCallback(
    async (romId: string) => {
      const saves = await listSaves(romId, MAX_SAVES_PER_ROM);
      return saves.find((save) => save.type === 'auto') ?? null;
    },
    [listSaves]
  );

  const deleteState = useCallback(
    async (save: CloudSaveListItem) => {
      if (save.status === 'pending') {
        await saveQueue.remove(save.id);
        return;
      }

      if (!user) {
        throw new Error('Sign in to manage cloud saves.');
      }

      const { error } = await supabaseAny
        .from('emulator_save_states')
        .delete()
        .eq('id', save.id)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      const paths = [save.storage_path, save.screenshot_path].filter(Boolean) as string[];
      if (paths.length > 0) {
        const { error: storageError } = await supabase.storage.from(SAVE_BUCKET).remove(paths);
        if (storageError) {
          throw storageError;
        }
      }
    },
    [saveQueue, supabaseAny, user]
  );

  const retryPendingSaves = useCallback(async () => {
    await saveQueue.syncPending();
  }, [saveQueue]);

  return {
    listSaves,
    saveState,
    loadStateBlob,
    getLatestAutosave,
    deleteState,
    retryPendingSaves,
    pendingSaves: saveQueue.entries,
    isQueueLoaded: saveQueue.isLoaded,
    isSyncingPendingSaves: saveQueue.isSyncing,
  };
};
